export type LegalSection = {
  heading: string;
  paragraphs: string[];
};

export type LegalDoc = {
  title: string;
  effectiveDate: string;
  sections: LegalSection[];
};

export const termsDoc: LegalDoc = {
  title: "Terms and Conditions",
  effectiveDate: "September 11, 2026",
  sections: [
    {
      heading: "1. Acceptance of Terms",
      paragraphs: [
        'These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User") and the operators of Agent FOMO ("we," "us," or "our"). By accessing or using the Agent FOMO platform across any channel — including WhatsApp messaging, our website at f0mo.com, the web app at hi.f0mo.com, or the developer API (collectively, the "Service") — you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.',
        "If you do not agree to these Terms, you must discontinue use of the Service immediately. The conversational agent provides links to these Terms upon your first interaction.",
      ],
    },
    {
      heading: "2. Description of Service & AI Nature",
      paragraphs: [
        "Agent FOMO is an automated artificial-intelligence service for cultural event discovery, recommendations, and nightlife guidance. The agent is powered by automated language models and is not a human, travel agent, ticketing agency, or venue representative.",
        "Responses from the AI agent are generated automatically and may occasionally contain inaccuracies or outdated information. Users should independently verify critical event details (such as dates, venues, door policies, and ticket prices) with original organizers or ticketing providers before making commitments or financial transactions.",
      ],
    },
    {
      heading: "3. Mental Health, Safety & Crisis Disclaimers",
      paragraphs: [
        "Agent FOMO is strictly an entertainment, cultural, and nightlife guide. It is NOT a healthcare, medical, psychological, or crisis intervention service. It does not provide medical advice, diagnosis, therapy, or emergency counseling.",
        "If you or someone around you is in emotional distress, experiencing thoughts of self-harm, or facing a mental health crisis, please immediately contact human professionals, local emergency services, or designated crisis hotlines in your country (such as ER'AN at 1201 or SAHAR at sahar.org.il in Israel, Telefonseelsorge in Germany, or 988 in the United States).",
        "The Service is automated and not continuously monitored by humans in real time. We make no representation that the Service will detect crisis situations or alert authorities. However, if clear and specific notice of an imminent threat to human life or safety comes to our attention, the operators reserve the right, without obligation, to provide relevant contact details to authorized emergency or law enforcement personnel to the extent necessary to prevent harm.",
      ],
    },
    {
      heading: "4. Event Data and Accuracy",
      paragraphs: [
        "Event listings are compiled automatically from public websites, social media channels, venues, ticketing portals, and user submissions. While we make reasonable efforts to maintain accuracy, event details are subject to change or cancellation by organizers without notice.",
        "Agent FOMO assumes no liability for errors, schedule changes, cancellations, pricing discrepancies, or any loss or inconvenience resulting from reliance on event information displayed by the Service.",
      ],
    },
    {
      heading: "5. Eligibility and Age Requirements",
      paragraphs: [
        "The Service is intended for individuals aged 16 and older. By using the Service, you represent and warrant that you are at least 16 years of age.",
        "Users aged 16 and 17 are welcome to use the Service with parental or guardian awareness where required by law. The Service restricts recommendations for adult-only (18+) or sexually oriented events from being served to minors. If we determine that a user is under 16, their account and associated data will be promptly removed.",
      ],
    },
    {
      heading: "6. User Conduct and Acceptable Use",
      paragraphs: [
        "You agree to use the Service only for lawful purposes in accordance with applicable laws, including consumer protection, privacy, and intellectual property statutes.",
        "You may not: (a) send spam, abusive, defamatory, or harassing messages; (b) attempt to reverse-engineer, decompile, or extract the underlying models, prompts, or proprietary algorithms of the Service; (c) bypass rate limits, quotas, or security measures; (d) scrape or systematically harvest event data outside authorized API terms; (e) submit false, misleading, or fraudulent events, coupons, or ticket listings; or (f) misuse contact details obtained through the Service for unsolicited marketing.",
      ],
    },
    {
      heading: "7. Paid Tiers, Subscriptions, and Promotion Credits",
      paragraphs: [
        "Basic use of the Service is free under standard monthly message and recommendation quotas. Users may upgrade to our unlimited Premium tier or purchase promotion credits for advertising events.",
        "Pricing, billing frequencies, and credit rates are clearly presented at the point of sale (in the chat interface or checkout screen) and are processed through secure third-party payment providers (such as Stripe). We do not store payment card credentials.",
        "Recurring subscriptions automatically renew at the end of each billing cycle unless cancelled prior to renewal. You may cancel recurring subscriptions at any time through the chat agent or by contacting us. In accordance with applicable consumer protection laws (including the Israeli Consumer Protection Law, 5741-1981), cancellation takes effect at the end of the current paid billing period with no further charges.",
      ],
    },
    {
      heading: "8. Second-Hand Ticket Exchange",
      paragraphs: [
        "The Service offers an informal peer-to-peer matching feature that connects users seeking tickets with users offering tickets for the same event by exchanging contact numbers upon mutual interest.",
        "Agent FOMO is NOT a party to ticket transactions. We do not sell, buy, hold, guarantee, or verify tickets, nor do we handle ticket payments or charge commissions. Completing the exchange, verifying ticket authenticity, and payment occur entirely between the parties outside our platform. Each transaction is conducted at the users' sole risk.",
        "To the maximum extent permitted by law, Agent FOMO disclaims all liability for counterfeit tickets, non-delivery, payment disputes, or event cancellations arising from peer-to-peer exchanges.",
      ],
    },
    {
      heading: "9. WhatsApp Messaging & Groups",
      paragraphs: [
        "When using the Service on WhatsApp, you consent to receive responses and notifications you requested. You may stop proactive notifications at any time by replying 'הסר' or 'STOP' in the chat.",
        "When our agent is invited to a WhatsApp group, the person adding the bot represents that they are an authorized group administrator. In group settings, the agent maintains discretion and never discloses any participant's private taste profile, interaction history, or personal data.",
      ],
    },
    {
      heading: "10. Event Submissions, Coupons, and Sponsored Recommendations",
      paragraphs: [
        "Organizers and promoters may submit events and attach discount coupon codes. Events offering genuine coupons may receive boosted visibility in recommendations. Promoters represent that submitted events and coupons are valid and agree to honor published promotions.",
        "Paid recommendations and promotional pushes are visibly labeled as sponsored content (e.g. '🤝 ממומן' / 'Sponsored') to ensure full transparency. Advertisers choose target criteria only and never receive access to personal user identities or contact details.",
      ],
    },
    {
      heading: "11. Developer API",
      paragraphs: [
        "The public Developer API allows querying events as documented at f0mo.com/llms.txt and hi.f0mo.com/docs. API tokens provided by the agent are personal and must remain confidential. Use is subject to rate limits and fair-use guidelines.",
      ],
    },
    {
      heading: "12. Intellectual Property",
      paragraphs: [
        "All software, designs, text, trademarks, and logos associated with Agent FOMO are the property of the operators or their licensors. You receive a personal, revocable, non-exclusive license to use the Service for personal, non-commercial purposes.",
      ],
    },
    {
      heading: "13. Disclaimer of Warranties",
      paragraphs: [
        'The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, express or implied. We do not warrant that the Service will be uninterrupted, error-free, secure, or that recommendations will meet individual expectations.',
      ],
    },
    {
      heading: "14. Limitation of Liability",
      paragraphs: [
        "To the fullest extent permitted by applicable law, the operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages, loss of profits, or data loss. In all events, our total cumulative liability shall not exceed the amount paid by you to the Service in the twelve months preceding the claim, or 100 ILS, whichever is greater.",
      ],
    },
    {
      heading: "15. Governing Law and Jurisdiction",
      paragraphs: [
        "These Terms shall be governed by and construed in accordance with the laws of the State of Israel. Any dispute arising out of or in connection with these Terms or the Service shall be subject to the exclusive jurisdiction of the competent courts in Tel Aviv-Jaffa, Israel.",
      ],
    },
    {
      heading: "16. Contact Information",
      paragraphs: [
        "For questions, support, or inquiries regarding these Terms, you can contact the operators on WhatsApp at +972-50-424-4206 or by messaging the agent with 'I want to talk to the operators'.",
      ],
    },
  ],
};

export const privacyDoc: LegalDoc = {
  title: "Privacy Policy",
  effectiveDate: "September 11, 2026",
  sections: [
    {
      heading: "1. Introduction",
      paragraphs: [
        'The operators of Agent FOMO ("we," "us," or "our") are committed to protecting your privacy and handling your personal information responsibly. This Privacy Policy describes how we collect, use, store, and protect your information when you interact with Agent FOMO across WhatsApp, web apps, and APIs.',
        "By using the Service, you acknowledge the data practices described in this policy. If you do not agree with these practices, please discontinue use of the Service.",
      ],
    },
    {
      heading: "2. Information We Collect",
      paragraphs: [
        "We collect only the information necessary to provide and personalize our event discovery service: (a) Contact Information: your WhatsApp phone number, display name, and optional email if provided; (b) Location Preferences: approximate geographic areas or cities you indicate to find relevant local events; (c) Taste & Interaction History: event categories, likes, dislikes, search queries, and swipes used to train your recommendation model; (d) Technical Data: standard device, browser, and IP information collected automatically on our website.",
        "Sensitive Information: Certain optional preferences (such as accessibility requirements, birth date for age-appropriate filtering, or interest in adult/LGBTQ+ events) are collected only when voluntarily shared and explicitly approved in conversation. Inferred Preferences: Our AI may infer category or venue affinities from chat interactions. You may inspect, correct, or request deletion of your stored taste profile at any time.",
      ],
    },
    {
      heading: "3. How We Use Information",
      paragraphs: [
        "We use your information to: (a) operate and maintain the Service, delivering personalized event recommendations; (b) send alerts, digests, or ticket notifications you explicitly subscribed to; (c) connect ticket buyers and sellers by exchanging phone numbers only upon mutual trade requests; (d) compile anonymized aggregate analytics regarding scene activity and popularity; and (e) detect and prevent abuse, spam, or technical issues.",
      ],
    },
    {
      heading: "4. Legal Basis for Processing",
      paragraphs: [
        "We process personal data under applicable law (including the Israeli Protection of Privacy Law, 5741-1981) based on: (a) your explicit consent; (b) contractual necessity to deliver the features you request; (c) our legitimate interest in operating and enhancing our cultural guide platform; and (d) compliance with legal obligations.",
      ],
    },
    {
      heading: "5. Information Sharing and Disclosure",
      paragraphs: [
        "We do NOT sell your personal data. We disclose information only in limited circumstances: (a) Service Providers: trusted cloud, hosting, and analytics infrastructure providers under confidentiality terms; (b) Ticket Counterparties: when a ticket trade match is confirmed, your name and phone number are shared solely with the matched counterparty to complete the trade; (c) Legal Requirements: when required by enforceable subpoena, warrant, or statutory requirement; and (d) Aggregate Insights: non-identifiable, aggregated statistics displayed on public dashboards.",
      ],
    },
    {
      heading: "6. Sponsored Content and Direct Marketing Opt-Out",
      paragraphs: [
        "Event organizers can promote events to matched audiences. Paid recommendations are visibly marked as sponsored in the chat. Advertisers receive only aggregate performance metrics and never receive your identity or contact details.",
        "You can opt out of proactive messages and promotional notifications at any time by replying 'הסר' or 'STOP' in the WhatsApp chat. Under the Israeli Protection of Privacy Law, you have the statutory right to request removal from any direct marketing database.",
      ],
    },
    {
      heading: "7. Non-User Data in Ticket Matching",
      paragraphs: [
        "To assist ticket discovery, the Service may process publicly posted event ticket requests (such as public group posts). Such data is used exclusively to facilitate active trades and is not used for marketing. Anyone whose details were processed this way can reply 'הסר' or contact us for prompt deletion.",
      ],
    },
    {
      heading: "8. Data Retention and Deletion",
      paragraphs: [
        "We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy or as required by law.",
        "You may request permanent erasure of your profile, taste history, and subscriptions at any time by using the data deletion tool in the chat (or telling the agent 'delete my data'), followed by sending /reset to clear conversation memory.",
      ],
    },
    {
      heading: "9. Data Security",
      paragraphs: [
        "We implement technical and organizational measures to safeguard your personal information against unauthorized access, loss, or alteration. While we maintain rigorous security standards, no internet transmission is completely immune to vulnerabilities.",
      ],
    },
    {
      heading: "10. Children's Privacy",
      paragraphs: [
        "The Service is not directed to children under 16. We do not knowingly collect personal data from individuals under 16. If we discover that personal data from a child under 16 has been gathered, we will delete it promptly.",
      ],
    },
    {
      heading: "11. Your Rights",
      paragraphs: [
        "Subject to applicable law, you have the right to access, inspect, correct, or request deletion of your personal data held in our systems, and to withdraw previously given consent. To exercise these rights, message the agent or contact us via WhatsApp.",
      ],
    },
    {
      heading: "12. Contact Information",
      paragraphs: [
        "For privacy inquiries, rights requests, or questions regarding our data practices, contact the operators on WhatsApp at +972-50-424-4206 or by messaging 'I want to talk to the operators'.",
      ],
    },
  ],
};

export const accessibilityDoc: LegalDoc = {
  title: "Accessibility Statement",
  effectiveDate: "September 11, 2026",
  sections: [
    {
      heading: "1. Our Commitment",
      paragraphs: [
        "The operators of Agent FOMO are committed to providing a digitally accessible experience for everyone, including people with disabilities. We strive to adhere to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA and Israeli Standard IS 5568.",
      ],
    },
    {
      heading: "2. Conversational Accessibility",
      paragraphs: [
        "Our primary service is delivered through natural language text chat on WhatsApp. Natural text messaging inherently supports assistive technologies, including screen readers (VoiceOver, TalkBack), voice dictation, operating system text enlargement, and high-contrast color schemes.",
        "Users can ask the agent for wheelchair-accessible events ('נגיש לכיסא גלגלים'). The agent records your accessibility preference and prioritizes verified accessible venues in future recommendations.",
      ],
    },
    {
      heading: "3. Website Accessibility Features",
      paragraphs: [
        "Our web platform includes the following accessibility measures: (a) Semantic HTML structure with proper heading hierarchies; (b) Keyboard navigation compatibility with visible focus indicators; (c) Sufficient color contrast ratios across dark mode layouts; (d) Responsive design adapting cleanly to high-zoom displays up to 200%; and (e) Alternative text for meaningful graphic elements.",
      ],
    },
    {
      heading: "4. Known Limitations",
      paragraphs: [
        "Some external event flyers and third-party venue links may not provide complete descriptive alt text. In such cases, the agent extracts and provides the essential event information in plain readable text. Third-party messaging capabilities remain subject to WhatsApp's platform accessibility features.",
      ],
    },
    {
      heading: "5. Feedback and Accessibility Coordinator",
      paragraphs: [
        "We welcome feedback regarding accessibility barriers or suggestions for improvement. Please reach out to our team via WhatsApp at +972-50-424-4206 or tell the agent in chat 'I have an accessibility request'. We strive to respond to accessibility inquiries within 5 business days.",
      ],
    },
  ],
};
